import { css, ThemeContext } from '@emotion/react';
import styled from '@emotion/styled';
import {
  blendAlphaToHex,
  convertHexToRGBA,
  convertHexToRGBIncludeAlpha,
  getHexColor,
} from 'presentations/views/helpers/color';
import { borderRadius, fontSize, fontWeight } from 'presentations/views/styles';
import {
  Appearance,
  colorPalette,
  gray02,
  gray05,
  white,
} from 'presentations/views/styles/color';
import {
  memo,
  DragEvent,
  useContext,
  SyntheticEvent,
  useMemo,
  ReactNode,
} from 'react';
import { CalendarMode } from './reducer';
import { CalendarContext } from './reducer/CalendarContext';
import { ResponseStatus } from 'domains/models/event/responseStatus';

interface Props {
  className?: string;
  eventId?: string;
  isDragging?: boolean;
  onDragStart?: (e: DragEvent<HTMLDivElement>) => void;
  onDragEnd?: (e: DragEvent<HTMLDivElement>) => void;
  name: string;
  icon?: ReactNode;
  color: string;
  x: number;
  xSpan?: number;
  y: number;
  ySpan: number;
  onClick?: (e: SyntheticEvent<HTMLDivElement>) => void;
  isDraggable?: boolean;
  isSelected?: boolean;
  responseStatus?: ResponseStatus;
  onDragHandlerMove?: (
    e: DragEvent<HTMLDivElement>,
    direction: 'left' | 'right',
  ) => void;
}
const Component = memo((props: Props) => {
  const { appearance }: any = useContext(ThemeContext);
  const { state } = useContext(CalendarContext);
  const isSelectable = state.calendarMode === CalendarMode.Event;

  const responseStatus = props.responseStatus || ResponseStatus.Accepted;

  const color = useMemo(() => {
    if (isSelectable) {
      return getHexColor(props.color) || state.userColor;
    } else {
      return colorPalette.gray06.www;
    }
  }, [props.color, isSelectable]);

  const backgroundColor = useMemo(() => {
    if (props.isSelected) {
      return convertHexToRGBA(color, 60);
    }
    if (appearance === Appearance.Dark) {
      return blendAlphaToHex(color, 40);
    } else {
      return convertHexToRGBIncludeAlpha(color, 24);
    }
  }, [color, props.isSelected, appearance]);

  const textColor = useMemo(() => {
    return color;
  }, [color]);

  const params = {
    x: props.x,
    xSpan: props.xSpan,
    y: props.y,
    ySpan: props.ySpan,
    backgroundColor: backgroundColor,
    isNeedsAction: responseStatus === ResponseStatus.NeedsAction,
    isTenative: responseStatus === ResponseStatus.Tentative,
  };

  const onDragStart = (e: DragEvent<HTMLDivElement>) => {
    const { eventId, dragDirection } = e.currentTarget.dataset;
    e.stopPropagation();
    if (dragDirection === 'left' || dragDirection === 'right') {
      if (typeof props.onDragHandlerMove === 'function') {
        props.onDragHandlerMove(e, dragDirection);
      }
    } else {
      if (typeof props.onDragStart === 'function') {
        props.onDragStart(e);
      }
    }
  };

  if (isSelectable === false) {
    return (
      <SlotEventCell {...params}>
        <Name>{props.name}</Name>
      </SlotEventCell>
    );
  } else {
    return (
      <EventCell
        {...params}
        className={props.className}
        data-event-id={props.eventId}
        draggable={props.isDraggable}
        isDragging={props.isDragging}
        onDragStart={onDragStart}
        onDragEnd={props.onDragEnd}
        onClick={props.onClick}
      >
        {/* <LeftDraghandler
          draggable
          data-event-id={props.eventId}
          data-drag-direction="left"
          onDragStart={onDragStart}
        /> */}
        <Content textColor={textColor} isSelectable={isSelectable}>
          <Name>
            {props.icon && <Icon>{props.icon}</Icon>}
            <NameText isDeclined={responseStatus === ResponseStatus.Declined}>
              {props.name}
            </NameText>
          </Name>
        </Content>
        {/* <RightDraghandler
          draggable
          data-event-id={props.eventId}
          data-drag-direction="right"
          onDragStart={onDragStart}
        /> */}
      </EventCell>
    );
  }
});

const EventCell = styled.div<{
  isDragging?: boolean;
  x: number;
  y: number;
  xSpan?: number;
  ySpan: number;
  backgroundColor: string;
  isTenative?: boolean;
  isNeedsAction?: boolean;
}>`
  grid-area: ${(props) =>
    `${props.y + 1} / ${props.x + 1} / span ${props.ySpan} / span ${
      props.xSpan || 1
    }`}; // 縦位置 / 横位置 / 縦枠幅 / 横枠幅
  cursor: pointer;
  /* width: calc(100% - 8px); */
  height: calc(100% - 2px);
  max-height: 16px;
  background-color: ${(props) => props.backgroundColor};
  background-image: ${(props) =>
    props.isNeedsAction
      ? `repeating-linear-gradient(-45deg, ${gray05(props)}, ${gray05(
          props,
        )} 4px, ${white(props)} 0px, ${white(props)} 8px)`
      : props.isTenative
      ? `repeating-linear-gradient(-45deg, ${props.backgroundColor}, ${
          props.backgroundColor
        } 4px, ${white(props)} 0px, ${white(props)} 8px)`
      : undefined};
  overflow: hidden;
  position: absolute;
  top: 1px;
  left: 0;
  right: 0;
  width: calc(100%);
  border-radius: ${borderRadius(4)};
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.05);
  ${(props) =>
    props.isDragging &&
    css`
      opacity: 0;
    `};
  display: flex;

  :hover {
    filter: brightness(96%);
  }
`;
const Content = styled.div<{
  textColor: string;
  isSelectable?: boolean;
}>`
  color: ${(props) => props.textColor};
  opacity: ${(props) => (props.isSelectable ? 1 : 0.6)};
  filter: brightness(50%);
  ${(props: any) =>
    props.theme.appearance === 'dark' &&
    css`
      filter: brightness(100%);
    `};
`;
const Name = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(11)};
  margin: 2px 0 0 6px;
  line-height: 1.2;
`;
const NameText = styled.span<{ isDeclined?: boolean }>`
  text-decoration: ${(props) => props.isDeclined && `line-through`};
`;
const Icon = styled.span`
  margin-right: 1px;
  vertical-align: middle;
`;
const SlotEventCell = styled(EventCell)`
  user-select: none;
  pointer-events: none;
  background-color: ${gray05};
  color: ${gray02};
`;
const LeftDraghandler = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 8px;
  left: 0;
  height: 100%;
  cursor: ew-resize;
  z-index: 2;
`;
const RightDraghandler = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 8px;
  right: 0;
  height: 100%;
  cursor: ew-resize;
  z-index: 2;
`;
export default Component;
